import {KeyDataForm} from "../../api/Model";
import {
    Box,
    Checkbox,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import React, {useCallback, useMemo} from "react";
import {inputChange, selectChange, SetState} from "../../util/FormHandlers";
import {NavLink} from "react-router-dom";
import {AppPages, usePageUrl} from "../../app-layout/AppPages";
import {useKeyTagStore} from "../../api/KeyTagStore";
import {KeyTagChip} from "../../components/key-tag-chip/KeyTagChip";
import {associateBy} from "../../util/MapUtil";
import {useRemoteValue} from "../../util/RemoteValue";


export function EditKeyForm({form, setForm}: {
    form: KeyDataForm,
    setForm: SetState<KeyDataForm>,
}) {
    const pageUrl = usePageUrl();
    const keyTagStore = useKeyTagStore();
    const keyTags = useRemoteValue(keyTagStore.remoteValue, []);

    const handlers = useMemo(() => ({
        keyNumberChange: inputChange(
            setForm,
            (newValue) => ({keyNumber: newValue})
        ),
        transponderIdChange: inputChange(
            setForm,
            (newValue) => ({transponderId: newValue || null})
        ),
    }), [setForm])

    const keyTagMap = useMemo(
        () => associateBy(keyTags, tag => tag.id),
        [keyTags],
    );

    const renderKeyTagsValue = useCallback(
        (selectedTagsIds: number[]) => (
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                {selectedTagsIds.map((selectedTagId) => {
                    const selectedTag = keyTagMap.get(selectedTagId);
                    return (
                        selectedTag
                            ? <KeyTagChip key={selectedTagId} keyTag={selectedTag}/>
                            : null
                    );
                })}
            </Box>
        ),
        [keyTagMap],
    );


    const handleKeyTagChange = selectChange(
        setForm,
        (selectedTagIds: number[] | string) => ({
            tags: (selectedTagIds as number[]).map(tagId => keyTagMap.get(tagId)!),
        }),
    );

    return (
        <Stack spacing={2} pt={2} pb={2}>
            <TextField
                autoFocus
                margin="dense"
                id="keyNumber"
                label="Key Number"
                fullWidth
                variant="outlined"
                required
                value={form.keyNumber}
                onChange={handlers.keyNumberChange}
            />
            <TextField
                margin="dense"
                id="transponderId"
                label="Transponder ID"
                fullWidth
                variant="outlined"
                value={form.transponderId || ''}
                onChange={handlers.transponderIdChange}
            />
            <FormControl fullWidth variant="outlined">
                <InputLabel id="key-tags">Tags</InputLabel>
                <Select
                    multiple
                    fullWidth
                    labelId="key-tags"
                    label="Tags"
                    value={form.tags.map((tag) => tag.id)}
                    onChange={handleKeyTagChange}
                    input={<OutlinedInput label="Tags"/>}
                    renderValue={renderKeyTagsValue}
                >
                    {keyTags.map((tag) => (
                        <MenuItem key={tag.id} value={tag.id}>
                            <Checkbox checked={form.tags.some((t) => t.id === tag.id)}/>
                            <ListItemText primary={tag.name}/>
                        </MenuItem>
                    ))}
                    <Box sx={{textAlign: 'center'}}>
                        <NavLink to={pageUrl(AppPages.keyTags)}>
                            <Typography variant="overline">Manage tags</Typography>
                        </NavLink>
                    </Box>
                </Select>
            </FormControl>
        </Stack>
    );
}
